import { Box, Skeleton, Typography } from "@mui/material";
import { useSyndeticsConfig } from "@tlc/ls2pac/components/SyndeticsConfig";
import { useEffect, useMemo, useState } from "react";
import { memo } from "react";
import { CoverSize, SyndeticsUnboundUtil } from "@tlc/syndetics-util";
import {
  getISBNList,
  getUPCList,
} from "@tlc/ls2pac/app/utils/PACStandardNumberUtil";
import { ResourceView } from "@tlc/ls2pac/dto/ResourceView";
import { ResourceDWR } from "@tlc/ls2pac/dto/ResourceDWR";
import * as React from "react";

function loadImage(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve(url);
    };
    img.onerror = reject;
    img.src = url;
  });
}

interface ResultBookjacketProps {
  resource: ResourceView | ResourceDWR;
  width: string | number; // FIXME there should be a type for "auto" | number, etc.
  // TODO allow setting only one dimension
  height: number;
  sx?: object;
}

export const ResultBookjacket = memo(
  ({ resource, width, height, sx }: ResultBookjacketProps) => {
    const { syndeticsConfig } = useSyndeticsConfig();
    const [status, setStatus] = useState<"loading" | "loaded" | "failed">(
      "loading"
    );

    const url = useMemo(() => {
      // TODO url should be an array of images to try and fallback to

      if (resource.localSmallImage) {
        return resource.localSmallImage;
      }

      // fall through to localLargeImage if no small is provided
      if (resource.localLargeImage) {
        return resource.localLargeImage;
      }

      if (syndeticsConfig?.active && syndeticsConfig?.clientCode) {
        // TODO ask syndetics if they have thresholds for width or height for resolving CoverSize vs hard coding to MD
        return SyndeticsUnboundUtil.getCoverImage(
          syndeticsConfig.clientCode,
          getISBNList(resource.standardNumbers),
          resource.shortTitle,
          CoverSize.MD,
          getUPCList(resource.standardNumbers),
          resource.shortAuthor
        );
      }

      return null;
    }, [
      syndeticsConfig?.active,
      syndeticsConfig?.clientCode,
      width,
      height,
      resource.id,
    ]);

    useEffect(() => {
      console.log("Rendering result bookjacket", resource.id, url);
      if (url) {
        loadImage(url)
          .then(() => {
            // TODO test image response against width/height thresholds
            setStatus("loaded");
          })
          .catch(() => {
            setStatus("failed");
          });
      } else {
        setStatus("failed");
      }
    }, [url]);

    return (
      <Box
        sx={{
          ...sx,
          width,
          height,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {status === "loading" ? (
          <Skeleton variant="rectangular" width={width} height={height} />
        ) : status === "failed" ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              background: "var(--mui-palette-grey-300)",
              p: 1,
            }}
          >
            <Typography
              variant="caption"
              component="span"
              sx={{
                fontWeight: "bold",
                fontSize: "0.75rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 4,
                WebkitBoxOrient: "vertical",
                maxHeight: "100%",
              }}
            >
              {resource.shortTitle}
            </Typography>
          </Box>
        ) : (
          <Box
            component="img"
            src={url}
            alt={`Cover of ${resource.shortTitle}`}
            sx={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        )}
      </Box>
    );
  },
  (prevProps, nextProps) =>
    prevProps.resource.id === nextProps.resource.id &&
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height
);
